// Status normalization utility to handle inconsistent status formats
// This ensures consistent status handling across the entire application

// Map of all possible status variations to their canonical form
const STATUS_MAP = {
  // not_submitted variations
  'not_submitted': 'not_submitted',
  'not submitted': 'not_submitted',
  'notsubmitted': 'not_submitted',
  'not-submitted': 'not_submitted',

  // CityOnly variations
  'cityonly': 'CityOnly',
  'city_only': 'CityOnly',
  'city-only': 'CityOnly',
  'city only': 'CityOnly',

  // Other statuses
  'booked': 'booked',
  'confirmed': 'confirmed',
  'pending': 'pending',
  'submitted': 'submitted',
  'needs_attention': 'needs_attention',
  'cancelled': 'cancelled',
};

// Canonical status constants to use throughout the app
export const BOOKING_STATUS = {
  NOT_SUBMITTED: 'not_submitted',
  CITY_ONLY: 'CityOnly',
  BOOKED: 'booked',
  CONFIRMED: 'confirmed',
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  NEEDS_ATTENTION: 'needs_attention',
  CANCELLED: 'cancelled',
};

/**
 * Normalizes a status string to its canonical form
 * @param {string} status - The status to normalize
 * @returns {string} - The normalized status
 */
export const normalizeBookingStatus = (status) => {
  if (!status || status === '') {
    return BOOKING_STATUS.NOT_SUBMITTED;
  }

  // Convert to lowercase and trim whitespace
  const normalizedInput = status.toString().toLowerCase().trim();

  // Return mapped value or default to not_submitted if unknown
  return STATUS_MAP[normalizedInput] || BOOKING_STATUS.NOT_SUBMITTED;
};

/**
 * Checks if a status indicates a booking needs action
 * @param {string} status - The status to check
 * @returns {boolean} - True if the booking needs action
 */
export const needsAction = (status) => {
  const normalized = normalizeBookingStatus(status);
  return normalized === BOOKING_STATUS.NOT_SUBMITTED ||
         normalized === BOOKING_STATUS.CITY_ONLY ||
         normalized === BOOKING_STATUS.NEEDS_ATTENTION;
};

/**
 * Checks if a status indicates a completed booking
 * @param {string} status - The status to check
 * @returns {boolean} - True if the booking is complete
 */
export const isBookingComplete = (status) => {
  const normalized = normalizeBookingStatus(status);
  return normalized === BOOKING_STATUS.BOOKED ||
         normalized === BOOKING_STATUS.CONFIRMED;
};

/**
 * Checks if a status indicates a pending booking
 * @param {string} status - The status to check
 * @returns {boolean} - True if the booking is pending
 */
export const isBookingPending = (status) => {
  const normalized = normalizeBookingStatus(status);
  return normalized === BOOKING_STATUS.PENDING ||
         normalized === BOOKING_STATUS.SUBMITTED;
};